import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Upload, ImageIcon, Search, Link2, Trash2, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { id: 'upload', label: 'Upload', icon: Upload },
  { id: 'library', label: 'My Library', icon: ImageIcon },
  { id: 'stock', label: 'Stock Photos', icon: Search },
  { id: 'url', label: 'Paste URL', icon: Link2 },
];

// Resolve a media-library relative URL to an absolute one
const absUrl = (u) => (u && u.startsWith('/') ? `${BACKEND_URL}${u}` : u);

/**
 * Universal image picker used across the site (publications, books, blog, classes...).
 * Tabs: Upload (-> object storage), My Library, Stock Photos (Unsplash), Paste URL.
 * Calls onSelect(url) with a final absolute image URL.
 */
export default function MediaPickerModal({ isOpen, onClose, onSelect }) {
  const [tab, setTab] = useState('upload');
  const [uploading, setUploading] = useState(false);
  const [library, setLibrary] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [stockQuery, setStockQuery] = useState('');
  const [stockResults, setStockResults] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [urlValue, setUrlValue] = useState('');

  const loadLibrary = useCallback(async () => {
    setLoadingLibrary(true);
    try {
      const res = await axios.get(`${API}/media-library`, { withCredentials: true });
      setLibrary(res.data.items || []);
    } catch (e) {
      console.error('Failed to load library:', e);
      toast.error('Failed to load your media library');
    } finally {
      setLoadingLibrary(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && tab === 'library') loadLibrary();
  }, [isOpen, tab, loadLibrary]);

  if (!isOpen) return null;

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    setUploading(true);
    try {
      const res = await axios.post(`${API}/media-library/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      toast.success('Uploaded and saved to your library');
      onSelect(absUrl(res.data.url));
      onClose();
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Remove this image from your library?')) return;
    try {
      await axios.delete(`${API}/media-library/${id}`, { withCredentials: true });
      setLibrary((prev) => prev.filter((i) => i.id !== id));
      toast.success('Removed from library');
    } catch (err) {
      toast.error('Failed to remove image');
    }
  };

  const searchStock = async (e) => {
    e?.preventDefault();
    if (!stockQuery.trim()) return;
    setStockLoading(true);
    try {
      const res = await axios.get(`${API}/unsplash/search`, {
        params: { query: stockQuery, page: 1, per_page: 12 },
      });
      setStockResults(res.data.results || []);
      if ((res.data.results || []).length === 0) toast.info('No images found. Try other terms.');
    } catch (err) {
      console.error('Stock search failed:', err);
      toast.error(err.response?.status === 429 ? 'Rate limit hit, try again shortly.' : 'Search failed');
    } finally {
      setStockLoading(false);
    }
  };

  const selectStock = async (photo) => {
    try {
      if (photo.links?.download_location) {
        await axios.post(`${API}/unsplash/track-download`, null, {
          params: { download_location: photo.links.download_location },
        });
      }
    } catch (e) { /* non-blocking */ }
    onSelect(photo.urls.regular);
    onClose();
  };

  const useUrl = () => {
    if (!urlValue.trim()) {
      toast.error('Please paste an image URL');
      return;
    }
    onSelect(urlValue.trim());
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4"
      data-testid="media-picker-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-xl font-bold text-[#1B4B5A]">Choose an image</h3>
          <button onClick={onClose} data-testid="media-picker-close" className="text-gray-500 hover:text-gray-800">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-2">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`media-tab-${id}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
                tab === id ? 'border-[#B5813B] text-[#B5813B]' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {tab === 'upload' && (
            <div>
              <label
                htmlFor="media-upload-input"
                className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-[#B5813B]/50 rounded-xl py-16 cursor-pointer hover:bg-[#FDF6EC] transition-colors"
              >
                {uploading ? (
                  <Loader2 className="w-10 h-10 text-[#B5813B] animate-spin" />
                ) : (
                  <Upload className="w-10 h-10 text-[#B5813B]" />
                )}
                <span className="text-gray-700 font-semibold">
                  {uploading ? 'Uploading…' : 'Click to upload an image'}
                </span>
                <span className="text-sm text-gray-500">JPG, PNG, GIF, WEBP or SVG · up to 15MB · stored permanently</span>
              </label>
              <input
                id="media-upload-input"
                type="file"
                accept="image/*"
                onChange={handleUpload}
                disabled={uploading}
                className="hidden"
                data-testid="media-upload-input"
              />
            </div>
          )}

          {tab === 'library' && (
            <div>
              {loadingLibrary ? (
                <div className="text-center py-16"><Loader2 className="w-8 h-8 text-[#B5813B] animate-spin mx-auto" /></div>
              ) : library.length === 0 ? (
                <p className="text-center text-gray-500 py-16">No images yet. Upload one from the Upload tab.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="media-library-grid">
                  {library.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => { onSelect(absUrl(item.url)); onClose(); }}
                      data-testid={`media-library-item-${item.id}`}
                      className="group relative aspect-square rounded-lg overflow-hidden border-2 border-gray-200 hover:border-[#B5813B]"
                    >
                      <img src={absUrl(item.url)} alt={item.original_filename} className="w-full h-full object-cover" />
                      <span
                        onClick={(e) => handleDelete(item.id, e)}
                        className="absolute top-1 right-1 bg-white/90 rounded-full p-1 text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove"
                      >
                        <Trash2 className="w-4 h-4" />
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'stock' && (
            <div>
              <form onSubmit={searchStock} className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={stockQuery}
                  onChange={(e) => setStockQuery(e.target.value)}
                  placeholder="Search free stock photos (e.g. acupuncture, herbs)…"
                  className="flex-1 border-2 border-gray-300 rounded-lg px-4 py-2 text-gray-800 focus:outline-none focus:border-[#B5813B]"
                  data-testid="media-stock-query"
                />
                <button type="submit" className="bg-[#1B4B5A] text-white px-5 py-2 rounded-lg font-semibold hover:bg-[#143a45]">
                  Search
                </button>
              </form>
              {stockLoading ? (
                <div className="text-center py-12"><Loader2 className="w-8 h-8 text-[#B5813B] animate-spin mx-auto" /></div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {stockResults.map((photo) => (
                    <button
                      key={photo.id}
                      onClick={() => selectStock(photo)}
                      className="aspect-square rounded-lg overflow-hidden border-2 border-gray-200 hover:border-[#B5813B]"
                    >
                      <img src={photo.urls.small} alt={photo.alt_description || 'stock'} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'url' && (
            <div className="space-y-3">
              <label className="block font-semibold text-gray-700">Image URL</label>
              <input
                type="url"
                value={urlValue}
                onChange={(e) => setUrlValue(e.target.value)}
                placeholder="https://…"
                className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 text-gray-800 focus:outline-none focus:border-[#B5813B]"
                data-testid="media-url-input"
              />
              {urlValue.trim() && (
                <img src={urlValue} alt="preview" className="max-h-48 rounded-lg border border-gray-200" onError={(e) => { e.target.style.display = 'none'; }} />
              )}
              <button onClick={useUrl} data-testid="media-url-use" className="bg-[#1B4B5A] text-white px-5 py-2 rounded-lg font-semibold hover:bg-[#143a45]">
                Use this image
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
